import { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";
import { woodboxSchema } from "./cotationFormData.form";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { countryRequiresPostalCode } from "@/lib/postal-codes.util";
import { i18nMessage } from "@/lib/i18nMessage";

export const DeliveryContentSchema = z.object({
  wineName: z.string().min(1, i18nMessage("validation:required")).max(100),
  year: z.number({ coerce: true, message: i18nMessage("validation:required") }).optional(),
  alcoholPercent: z
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:required"))
    .max(100, i18nMessage("validation:required")),
  volume: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(0, i18nMessage("validation:required")),
  color: z.nativeEnum(WineColorEnum, { message: i18nMessage("validation:required") }),
  quantity: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(1, i18nMessage("validation:required")),
  price: z.coerce
    .number({ message: i18nMessage("validation:required"), coerce: true })
    .min(1, i18nMessage("validation:required")),
  productId: z.string().optional(),
  productUSAId: z.string().optional(),
  isEditing: z.boolean().optional(),
});

export const refineDeliveryPostalAndState = (
  data: {
    countryCode: string;
    stateOrProvinceCode?: string;
    postalCode?: string;
  },
  ctx: z.RefinementCtx,
) => {
  if (
    (data.countryCode === "CA" || data.countryCode === "US") &&
    (!data.stateOrProvinceCode || data.stateOrProvinceCode.length !== 2)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18nMessage("validation:stateOrProvinceRequired"),
      path: ["stateOrProvinceCode"],
    });
  }
  if (countryRequiresPostalCode(data.countryCode) && !data.postalCode) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18nMessage("validation:postalCodeRequiredForCountry"),
      path: ["postalCode"],
    });
  }
};

export const DeliveryFormSchema = z
  .object({
    company: z.string().optional(),
    lastName: z
      .string({
        required_error: i18nMessage("shared:forms.quotationDelivery.lastNameRequired"),
      })
      .min(1, i18nMessage("shared:forms.quotationDelivery.lastNameRequired")),
    firstName: z
      .string({
        required_error: i18nMessage("shared:forms.quotationDelivery.firstNameRequired"),
      })
      .min(1, i18nMessage("shared:forms.quotationDelivery.firstNameRequired")),
    email: z
      .string({
        required_error: i18nMessage("shared:forms.quotationDelivery.emailRequired"),
      })
      .trim()
      .email({ message: i18nMessage("validation:invalidEmail") }),
    phone: z
      .string({
        required_error: i18nMessage("shared:forms.quotationDelivery.phoneRequired"),
      })
      .refine((mobile) => isValidPhoneNumber(mobile), {
        message: i18nMessage("validation:invalidPhoneNumber"),
      }),
    street: z
      .string({
        required_error: i18nMessage("shared:forms.quotationDelivery.streetRequired"),
      })
      .min(1, i18nMessage("shared:forms.quotationDelivery.streetRequired")),
    postalCode: z.string().optional(),
    city: z
      .string({
        required_error: i18nMessage("shared:forms.quotationDelivery.cityRequired"),
      })
      .min(1, i18nMessage("shared:forms.quotationDelivery.cityRequired")),
    countryCode: z
      .string({
        message: i18nMessage("shared:forms.quotationDelivery.countryCodeRequired"),
      })
      .min(1, i18nMessage("shared:forms.quotationDelivery.countryCodeRequired")),
    stateOrProvinceCode: z.string().optional(),
    customId: z.string().min(1, i18nMessage("shared:forms.quotationDelivery.customIdRequired")),
    deliveryContent: z
      .array(DeliveryContentSchema)
      .min(1, i18nMessage("shared:forms.quotationDelivery.productsRequired")),
    woodBoxes: z.array(woodboxSchema),
    billingEntityId: z
      .string({
        required_error: i18nMessage("shared:forms.quotationDelivery.billingEntityRequiredError"),
        message: i18nMessage("shared:forms.quotationDelivery.billingEntityRequired"),
      })
      .min(1, i18nMessage("shared:forms.quotationDelivery.billingEntityRequired")),
    pickUpLocationId: z
      .string({
        required_error: i18nMessage("shared:forms.quotationDelivery.pickupLocationRequiredError"),
        message: i18nMessage("shared:forms.quotationDelivery.pickupLocationRequired"),
      })
      .min(1, i18nMessage("shared:forms.quotationDelivery.pickupLocationRequired")),
    comment: z.string().optional(),
    dapOption: z.boolean(),
    express: z.boolean(),
    insurance: z.boolean(),
    reference: z.string().optional(),
    newFilesId: z.array(z.string()).optional(),
    removedFilesId: z.array(z.string()).optional(),
  })
  .superRefine(refineDeliveryPostalAndState);

export type DeliveryFormData = z.infer<typeof DeliveryFormSchema>;

export type DeliveryContentData = z.infer<typeof DeliveryContentSchema>;

export const DeliveryFormOcrSchema = z.object({
  billingEntityId: z
    .string({
      required_error: i18nMessage("shared:forms.quotationDelivery.billingEntityRequiredError"),
      message: i18nMessage("shared:forms.quotationDelivery.billingEntityRequired"),
    })
    .min(1, i18nMessage("shared:forms.quotationDelivery.billingEntityRequired")),
  pickUpLocationId: z
    .string({
      required_error: i18nMessage("shared:forms.quotationDelivery.pickupLocationRequiredError"),
      message: i18nMessage("shared:forms.quotationDelivery.pickupLocationRequired"),
    })
    .min(1, i18nMessage("shared:forms.quotationDelivery.pickupLocationRequired")),
  comment: z.string().optional(),
  dapOption: z.boolean(),
  express: z.boolean(),
  insurance: z.boolean(),
  reference: z.string().optional(),
  invoiceFileId: z.string().optional(),
  newFilesId: z.array(z.string()).optional(),
  removedFilesId: z.array(z.string()).optional(),
});

export type DeliveryFormOcrData = z.infer<typeof DeliveryFormOcrSchema>;
